import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useMode } from "../context/ModeContext";

/**
 * "(click here)" hint that appears above the logo for 5 seconds whenever
 * the active studio changes (or on first mount). Colored with the current
 * mode's accent so users learn the logo is a button for switching studios.
 *
 * Behaviour
 *  - Shows for 5 seconds, then fades out.
 *  - Re-triggers each time the mode changes (so the hint reinforces
 *    "the logo is a button" in every realm).
 *  - Used by DashboardLayout above both the desktop and mobile logos.
 */
export default function LogoHint({ className = "" }) {
  const [visible, setVisible] = useState(true);
  let accent = "#FF4F27";
  try {
    const m = useMode();
    accent = m.config?.accent || accent;
  } catch (_) {
    /* Logo can render outside ModeProvider — keep coral fallback */
  }

  // Re-trigger the hint whenever the mode changes
  useEffect(() => {
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 5000);
    return () => clearTimeout(t);
  }, [accent]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key={`logo-hint-${accent}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className={`pointer-events-none select-none text-[10px] font-bold uppercase tracking-[0.18em] ${className}`}
          style={{ color: accent }}
          data-testid="logo-click-hint"
        >
          <motion.span
            animate={{ opacity: [1, 0.55, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex items-center gap-1"
          >
            <span aria-hidden="true">↓</span>
            (click here)
          </motion.span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
